import { MaterialCommunityIcons } from '@expo/vector-icons';
import React, { useEffect, useState } from 'react';
import { ScrollView, TouchableOpacity, View } from 'react-native';

import { Text } from 'react-native-paper';
import Toast from 'react-native-toast-message';
import { Colors } from '../../themes/colors';
import { SearchInput } from '../crop-circle/Components/SearchInput';
import { AddIcon, ChevronUpIcon, XIcon } from '../crop-circle/icons';
import { tokens } from '../crop-circle/tokens';
import { multiItemSelectorStore } from './multiItemSelectorStore';
import { themedStyleSheet } from '../../themes/themedStyleSheet';

export type Item = {
  id: string;
  name: string;
};

export type MultiItemSelectorProps = {
  selectedItems: Item[];
  availableItems: Item[];
  onItemsChange: (items: Item[]) => void;
  onCreateItem?: (name: string) => Promise<Item>;
  /** Set while creating is unavailable: dims the create button and a tap shows this instead of creating. */
  createLockedMessage?: string;
  disabled?: boolean;
  title?: string;
  subtitle?: string;
  placeholder: string;
  searchPlaceholder: string;
  createButtonText: string;
  containerBackgroundColor?: string;
  itemType?: 'group' | 'category' | 'label';
  emptyText?: string;
  onSelectorEdit?: () => void;
};

export const MultiItemSelector: React.FC<MultiItemSelectorProps> = ({
  selectedItems,
  availableItems,
  onItemsChange,
  onCreateItem,
  createLockedMessage,
  disabled = false,
  title,
  subtitle,
  searchPlaceholder,
  createButtonText,
  itemType,
  onSelectorEdit,
}) => {
  const [showOptions, setShowOptions] = useState(false);
  const [search, setSearch] = useState('');
  const [filteredItems, setFilteredItems] = useState<Item[]>([]);
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    const filtered = availableItems.filter(
      (item) =>
        item &&
        item.name &&
        item.name.toLowerCase().includes(search.toLowerCase()) &&
        !selectedItems.some((selected) => selected.id === item.id)
    );
    setFilteredItems(filtered);
  }, [availableItems, selectedItems, search]);

  const handleItemToggle = (item: Item) => {
    const isSelected = selectedItems.some((i) => i.id === item.id);
    if (isSelected) {
      onItemsChange(selectedItems.filter((i) => i.id !== item.id));
    } else {
      onItemsChange([...selectedItems, item]);
    }
  };

  const handleCreateItem = async () => {
    if (!search.trim() || !onCreateItem) return;

    setIsCreating(true);
    try {
      const newItem = await onCreateItem(search.trim());
      onItemsChange([...selectedItems, newItem]);
      setSearch('');
      setShowOptions(false);
    } catch (error) {
      console.error('Failed to create item:', error);
    } finally {
      setIsCreating(false);
    }
  };

  const handleRemoveItem = (itemId: string) => {
    onItemsChange(selectedItems.filter((i) => i.id !== itemId));
  };

  const canCreateItem =
    !!search.trim() &&
    !availableItems.some(
      (item) => item.name.toLowerCase() === search.trim().toLowerCase()
    );

  return (
    <View style={{ backgroundColor: 'transparent' }}>
      {title && <Text style={styles.titleCropCircle}>{title}</Text>}
      {subtitle && <Text style={styles.subtitle}>{subtitle}</Text>}

      {/* Dropdown Trigger with Selected Items */}
      <View style={[styles.pillsRow, disabled && styles.disabled]}>
        {selectedItems.map((item) => (
          <View key={item.id} style={styles.darkPill}>
            <Text style={styles.darkPillText} numberOfLines={1}>
              {item.name}
            </Text>
            {!disabled && (
              <TouchableOpacity
                onPress={() => handleRemoveItem(item.id)}
                hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
              >
                <XIcon size={12} color={Colors.role.textOnInverse} />
              </TouchableOpacity>
            )}
          </View>
        ))}
        {!disabled && (
          <TouchableOpacity
            style={styles.addButton}
            testID={
              itemType ? `multi-item-selector-add-${itemType}` : undefined
            }
            onPress={() => {
              if (onSelectorEdit) {
                multiItemSelectorStore.request(onItemsChange, selectedItems);
                onSelectorEdit();
              } else {
                setShowOptions(!showOptions);
              }
            }}
          >
            {showOptions ? (
              <ChevronUpIcon size={16} color={Colors.role.textStrong} />
            ) : (
              <AddIcon size={16} color={Colors.role.textStrong} />
            )}
          </TouchableOpacity>
        )}
      </View>

      {/* Dropdown Options */}
      {showOptions && (
        <View style={styles.optionsListMainContainer}>
          <SearchInput
            value={search}
            onChangeText={setSearch}
            placeholder={searchPlaceholder}
            height={44}
          />

          <View style={styles.optionsListContainer}>
            <ScrollView
              style={{ maxHeight: 400 }}
              contentContainerStyle={styles.itemsGrid}
              nestedScrollEnabled
            >
              {filteredItems.map((item) => (
                <TouchableOpacity
                  key={item.id}
                  style={styles.itemPill}
                  onPress={() => handleItemToggle(item)}
                >
                  <Text style={styles.itemPillText}>{item.name}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>

            {canCreateItem && onCreateItem && (
              <TouchableOpacity
                style={[
                  styles.createItemButton,
                  createLockedMessage ? styles.createItemLocked : undefined,
                ]}
                onPress={
                  createLockedMessage
                    ? () =>
                        Toast.show({ type: 'info', text1: createLockedMessage })
                    : handleCreateItem
                }
                accessibilityHint={createLockedMessage}
                disabled={isCreating}
              >
                <MaterialCommunityIcons
                  name="plus-circle-outline"
                  size={16}
                  color={Colors.primaryDark}
                  style={styles.createItemIcon}
                />
                <Text style={styles.createItemButtonText}>
                  {createButtonText} "{search.trim()}"
                </Text>
              </TouchableOpacity>
            )}
          </View>
        </View>
      )}
    </View>
  );
};

const styles = themedStyleSheet((role) => ({
  subtitle: {
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 16,
    marginBottom: 8,
    color: role.textSecondary,
  },
  titleCropCircle: {
    ...tokens.typography.label.largeEmphasised,
    marginBottom: tokens.spacing.xs,
    color: role.textStrong,
  },
  pillsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: tokens.spacing.sm,
    alignItems: 'center',
  },
  darkPill: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: role.surfaceInverse,
    borderRadius: tokens.borderRadius['3xl'],
    height: 40,
    paddingHorizontal: tokens.spacing.lg,
    gap: tokens.spacing.sm,
  },
  darkPillText: {
    ...tokens.typography.body.small,
    color: role.textOnInverse,
    maxWidth: 120,
  },
  addButton: {
    width: 40,
    height: 40,
    borderRadius: tokens.borderRadius['3xl'],
    backgroundColor: role.surfaceHover,
    alignItems: 'center',
    justifyContent: 'center',
  },
  disabled: {
    opacity: 0.5,
  },
  optionsListMainContainer: {
    marginTop: 8,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: role.borderEmphasis,
    backgroundColor: role.surface,
  },
  optionsListContainer: {
    marginBottom: 10,
    maxHeight: 400,
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  itemsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    rowGap: 4,
  },
  itemPill: {
    backgroundColor: role.surfaceHover,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 15,
    alignSelf: 'flex-start',
  },
  itemPillText: {
    fontSize: 14,
    lineHeight: 16,
    color: role.textStrong,
    fontWeight: '400',
  },
  createItemButton: {
    backgroundColor: role.surfaceHover,
    paddingHorizontal: 12,
    paddingVertical: 12,
    borderRadius: 8,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 8,
    width: '100%',
  },
  createItemLocked: {
    opacity: 0.5,
  },
  createItemIcon: {
    marginRight: 8,
  },
  createItemButtonText: {
    fontSize: 14,
    lineHeight: 16,
    color: Colors.primaryDark,
    fontWeight: '500',
  },
}));
